import React, { useEffect, useState, useRef } from "react";
import { Card, CardHeader, CardBody, ExpansionPanel, ExpansionPanelContent } from "@progress/kendo-react-layout";
import { Upload } from "@progress/kendo-react-upload";
import { Reveal } from "@progress/kendo-react-animation";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Label, Error } from "@progress/kendo-react-labels";
import { Input } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import Requests from "./Requests";
import Notification from "./Notification";
import { extensionTypes, specialCharacters } from "./components/data/constants";
import "./DocumentViewerStyles.scss";
import SkeletonDocumentViewer from "./components/Skeletons/SkeletonDocumentViewer";
import { Utils } from "./helpers";

const DocumentViewer = (props) => {
  const { core, appId, url } = props;
  const alertAdapter = core?.make("oxzion/alert");
  const loader = core.make("oxzion/splash");
  const helper = core.make("oxzion/restClient");
  const baseUrl = core.config("wrapper.url").endsWith("/") ? core.config("wrapper.url") : core.config("wrapper.url") + "/";
  let urlRes = url.split("/");
  const fileId = urlRes[urlRes.length - 2];
  const notif = React.createRef();
  const [isLoading, setIsLoading] = useState(true);
  const [documentsList, setDocumentsList] = useState({});
  const [selectedFolderName, setSelectedFolderName] = useState("Attachments");
  const [selectedDocument, setSelectedDocument] = useState(undefined);
  const [entityConfig, setEntityConfig] = useState(props.entityConfig ?? null);

  // check if the EOX office is enabled (ox_app) from the database and change the messages accordingly
  const apps = core.make("oxzion/profile").get().key.apps;
  const hasEOXOfficeApp = apps.some((i) => i.name === "EOXOffice");

  // Default to false
  let hasStorageType = false;

  if (core.configuration?.storage?.type === "onlyoffice") hasStorageType = hasEOXOfficeApp;
  if (core.configuration?.storage?.type === "sharepoint") hasStorageType = true;

  useEffect(() => {
    getDocumentsList();
    if (!entityConfig) fetchEntityDetails();
    //cleanup
    return () => {
      setDocumentsList({});
    };
  }, []);

  const fetchEntityDetails = () => {
    if (props.fileData?.entity_id) {
      helper.request("v1", "/app/" + props.appId + "/entity/" + props.fileData?.entity_id + "/page", {}, "get").then((response) => {
        if (response.status === "success" && response.data) setEntityConfig(response.data);
      });
    }
  };

  const findFolderForDocument = (documentsList, document) => {
    for (const [folderName, documents] of Object.entries(documentsList)) {
      if (documents.some(doc => doc.uuid === document.uuid)) {
        return folderName;
      }
    }
    return null;
  };

  const getDocumentsList = async (preferredDocument = null) => {
    try {
      setIsLoading(true);
      const response = await Requests.getDocumentsListService(core, appId, url);
      if (response.data) {
        const newDocumentsList = {};
        const newFolderType = {};

        Object.entries(response.data).forEach(([docType, data]) => {
          if (data && data.value && data.type) {
            if (data.value.length > 0 || data.type === "file") {
              newDocumentsList[docType] = data.value.length > 0 ? data.value : [];
              newFolderType[docType] = data.type;
            }
          }
        });

        setDocumentsList(newDocumentsList);
        
        // Set the selected folder based on preferred document or default to first folder
        if (preferredDocument) {
          const documentFolder = findFolderForDocument(newDocumentsList, preferredDocument);
          if (documentFolder) {
            setSelectedFolderName(documentFolder);
          }
          setSelectedDocument(preferredDocument);
        } else {
          setSelectedFolderName(Object.keys(newDocumentsList)[0]);
          if (newDocumentsList[Object.keys(newDocumentsList)[0]]?.[0]) {
            setSelectedDocument(newDocumentsList[Object.keys(newDocumentsList)[0]][0]);
          }
        }
      } else {
        notif?.current?.notify("", "No documents found!", "error", undefined, 3000);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      notif?.current?.notify("", "Failed to fetch documents. Please try again.", "error", undefined, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectDocument = (document) => {
    if (selectedDocument?.uuid === document?.uuid) return;
    setSelectedDocument(document);
  };

  const handleDelete = async (document, folderName) => {
    setSelectedFolderName(folderName);
    const messageBody = `Are you sure to delete ${document.originalName} ?`;
    alertAdapter
      ?.show({
        title: "Confirm Delete",
        text: messageBody,
        confirmButtonText: "Yes, delete",
        cancelButtonText: "No, cancel",
      })
      .then((result) => {
        if (result) {
          loader.show();
          Requests.deleteFile(core, appId, fileId, document.uuid, document.name).then((response) => {
            if (response.status == "success") {
              let tempIndex = undefined;
              documentsList[selectedFolderName] = documentsList[selectedFolderName].filter((item, index, array) => {
                if (item.uuid === document.uuid) {
                  index === array.length - 1 ? (tempIndex = index - 1) : (tempIndex = index);
                }
                return item.uuid !== document.uuid;
              });
              setSelectedDocument(documentsList[selectedFolderName][tempIndex] || undefined);
              setDocumentsList(documentsList);
              loader.destroy();
              alertAdapter.show({ title: "Attachment deleted successfully!", confirmButtonText: "Got it", icon: "success" });
            } else {
              loader.destroy();
              alertAdapter.show({ title: response.message, confirmButtonText: "Got it", icon: "error" });
            }
          });
        }
      });
  };

  // Function to replace filename in path and url in the last part
  const replaceFileName = (str, newFileName) => {
    return str.replace(/[^/]+$/, newFileName);
  };

  const getMessage = (data) => {
    if (data.message) return data.message;
    if (typeof data.data === "string") return JSON.parse(data.data)?.message;
    return null;
  };

  const removeExtension = (docName) => {
    return docName?.substring(0, docName.lastIndexOf(".")) || docName;
  };

  const handleRename = async (event, document, folderName) => {
    if (event.target.value !== removeExtension(document.name)) {
      setSelectedFolderName(folderName);

      alertAdapter?.show({
        title: "Initiating rename...",
        icon: "info",
        allowOutsideClick: false,
      });

      const payload = {
        name: document.originalName,
        old_name: document.name,
        url: document.url || "",
        path: document.path || "",
      };

      helper.request(
        "v1",
        `app/${appId}/file/${fileId}/attachment/${document.uuid}`,
        {
          payload,
          callbacks: {
            onOpen: () => {
              alertAdapter?.update({
                title: "Establishing connection... Renaming file. Please wait.",
                icon: "info",
                allowOutsideClick: false,
              });
            },
            onSuccess: (data) => {
              const updatedDocument = {
                ...document,
                name: document.originalName,
                path: replaceFileName(document.path, document.originalName),
                url: replaceFileName(document.url, document.originalName),
              };

              setSelectedDocument(updatedDocument);

              setDocumentsList((prev) => {
                const updatedDocumentsList = { ...prev };
                const folderDocuments = updatedDocumentsList[folderName];
                const documentIndex = folderDocuments.findIndex((doc) => doc.uuid === document.uuid);

                if (documentIndex !== -1) {
                  folderDocuments[documentIndex] = {
                    ...folderDocuments[documentIndex],
                    name: document.originalName,
                    originalName: document.originalName,
                  };
                }

                return updatedDocumentsList;
              });

              alertAdapter?.update({
                title: getMessage(data) || "Successfully renamed the document!",
                icon: "success",
                confirmButtonText: "Got it",
                allowOutsideClick: false,
              });
            },
            onError: (data) => {
              alertAdapter?.update({
                title: getMessage(data) || "Failed to rename document. Please try again.",
                icon: "error",
                confirmButtonText: "Got it",
                allowOutsideClick: false,
              });
            },
            onUpdateStatus: (data) => {
              alertAdapter?.update({
                title: getMessage(data) || "Processing...",
                icon: "info",
                allowOutsideClick: false,
              });
            },
          },
        },
        "SSE",
      );
    }
  };

  const handleShareLink = async (action, document = selectedDocument) => {
    if (core.configuration?.storage?.type === "sharepoint") {
      Utils.openShareLink({
        action,
        document,
        fileId,
        getMessage,
      });
    } else if (core.configuration?.storage?.type === "onlyoffice")
      core.run("EOXOffice", { appId: appId, fileId: fileId, docData: document, mode: action });
  };

  if (isLoading) {
    return <SkeletonDocumentViewer />;
  }

  return (
    <>
      <Notification ref={notif} />
      <div className="row document-viewer-container">
        <div className="col-md-4">
          {Object.keys(documentsList).length > 0 && (
            <DocumentFoldersPanel
              key={"DocumentFoldersPanel"}
              core={core}
              fileId={fileId}
              baseUrl={baseUrl}
              helper={helper}
              appId={appId}
              notif={notif}
              loader={loader}
              alertAdapter={alertAdapter}
              documentsList={documentsList}
              getDocumentsList={getDocumentsList}
              selectedDocument={selectedDocument}
              setSelectedDocument={setSelectedDocument}
              selectedFolderName={selectedFolderName}
              setSelectedFolderName={setSelectedFolderName}
              onSelectDocument={handleSelectDocument}
              onDeleteDocument={handleDelete}
              entityConfig={entityConfig}
              handleShareLink={handleShareLink}
              hasStorageType={hasStorageType}
            />
          )}
        </div>
        <div className="col-md-8">
          {selectedDocument ? (
            <DocumentView
              key={"DocumentView"}
              core={core}
              notif={notif}
              baseUrl={baseUrl}
              fileId={fileId}
              appId={appId}
              selectedDocument={selectedDocument}
              setSelectedDocument={setSelectedDocument}
              selectedFolderName={selectedFolderName}
              onDeleteDocument={handleDelete}
              entityConfig={entityConfig}
              handleShareLink={handleShareLink}
              handleRename={handleRename}
              hasStorageType={hasStorageType}
            />
          ) : (
            <Card>
              <CardBody className="text-center">
                <div className="row mt-2 document-viewer-container">
                  <div className="col-md-12">
                    <div className="my-5 py-5">
                      <h4>No files found</h4>
                      <br />
                      {hasStorageType && !entityConfig?.disable_create_upload && <p>You can upload files or create a new one using Office to get started.</p>}
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

// /////////////////////////////////////////////////////////////////////
const DocumentFoldersPanel = ({
  core,
  fileId,
  baseUrl,
  helper,
  appId,
  notif,
  loader,
  alertAdapter,
  documentsList,
  getDocumentsList,
  selectedDocument,
  setSelectedDocument,
  selectedFolderName,
  setSelectedFolderName,
  onSelectDocument,
  onDeleteDocument,
  entityConfig,
  handleShareLink,
  hasStorageType
}) => {
  const [visible, setVisible] = React.useState(false);
  const [selectedFiletype, setSelectedFiletype] = useState({ key: "Document", value: "docx" });
  const [fileName, setFileName] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileTypeOptions = [
    { key: "Document", value: "docx" },
    { key: "SpreadSheet", value: "xlsx" },
    { key: "Presentation", value: "pptx" },
  ];
  const uploadRef = useRef(null);
  const viewUrl = core.config("ui.url");

  // Used to set the selected folder name immediately
  useEffect(() => {
    // Only auto-select if no document is currently selected
    if (!selectedDocument && selectedFolderName && documentsList[selectedFolderName]?.length > 0) {
      const firstDoc = documentsList[selectedFolderName][0];
      if (firstDoc?.uuid) {
        onSelectDocument(firstDoc);
      }
    }
  }, [selectedFolderName, documentsList]);

  const handleCreate = async (fileName, type) => {
    if (!fileName) {
      notif?.current?.notify("", "Please enter a file name.", "error", undefined, 3000);
      return;
    }

    try {
      loader.show();
      const newDocument = `${fileName}.${type}`;
      const fetchedFile = await fetch(`${viewUrl}template/new.${type}`);
      const blob = await fetchedFile.blob();
      const templateFile = new File([blob], newDocument);
      const fileExists = documentsList[selectedFolderName]?.some((doc) => doc.name === newDocument);

      if (fileExists) {
        alertAdapter.show({
          title: "Failed to upload!",
          text: `File name ${newDocument} already exists.`,
          confirmButtonText: "Got it",
          icon: "error",
        });
        return;
      }

      const filesData = [
        {
          name: templateFile.name,
          type: `file/${type}`,
          fileId,
          fieldLabel: selectedFolderName,
        },
      ];

      const response = await postAttachments({ filesData, files: [templateFile] }, true);

      if (response?.status === "success") {
        const preferredDocument = response.data?.length > 0 ? response.data[0] : null;
        getDocumentsList(preferredDocument);
        if (preferredDocument) {
          handleShareLink("edit", preferredDocument);
        }
      } else {
        alertAdapter.show({ title: "File Creation failed.", confirmButtonText: "Got it", icon: "error" });
      }
    } catch (error) {
      alertAdapter.show({ title: "File Creation failed.", confirmButtonText: "Got it", icon: "error" });
    } finally {
      loader.destroy();
      toggleDialog();
      setFileName("");
    }
  };

  const postAttachments = async (allFileData) => {
    let response = await helper.request("v1", "/app/" + appId + "/file/attachment", allFileData, "fileupload");
    return response;
  };

  const handleExpansion = (name) => {
    // Toggle behavior: if clicking the same folder, collapse it (set to null)
    const newSelectedFolder = selectedFolderName === name ? null : name;
    setSelectedFolderName(newSelectedFolder);
    
    // Only select document if we're expanding (not collapsing)
    if (newSelectedFolder && documentsList[newSelectedFolder]?.[0]) {
      onSelectDocument(documentsList[newSelectedFolder][0]);
    }
  };

  const toggleDialog = () => {
    setVisible(!visible);
  };

  const shortenFilename = (name) => {
    return name.substring(0, 12) + "..." + name.split(".")[1];
  };

  const CustomTitle = ({ name }) => {
    return (
      <div className="d-flex justify-content-between align-items-center w-100 p-0">
        <i className="fa fa-folder mr-2"></i>
        <span className="custom-title">{name}</span>
      </div>
    );
  };

  const convertFromEscape = (escapedString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(escapedString, "text/html");
    return doc.documentElement.textContent;
  };

  const onFileChange = (event) => {
    if (!event || !event.newState) {
      return;
    }
    const validFiles = event.newState.filter((item) => !item.validationErrors?.length);
    const fileError = event.newState.length !== validFiles.length;

    if (validFiles && validFiles.length > 0) {
      validFiles.forEach((each) => {
        each.name = convertFromEscape(each.name)
          ?.replace(specialCharacters, "")
          ?.replace(/[\[\\\]]/g, ""); // Simplified regex for [ \ ]
      });
      uploadAttachments(validFiles);
    }
    if (fileError) {
      console.log("🚀 ~ onFileChange ~ fileError:", fileError);
      alertAdapter.show({
        title: "Unsupported File",
        confirmButtonText: "Got it",
        icon: "error",
      });
    }
  };

  const uploadAttachments = async (files) => {
    loader.show();

    let fileList = documentsList[selectedFolderName]?.map((a) => a.name);
    let uniqueFiles = files.filter((file) => !fileList?.includes(file.name));
    let duplicateFiles = files.filter((file) => fileList?.includes(file.name));

    if (duplicateFiles.length > 0) {
      alertAdapter.show({
        title: "Skipping duplicate(s): ",
        text: `${duplicateFiles.map((f) => f.name).join(", ")}`,
        confirmButtonText: "Got it",
      });
    }
    if (uniqueFiles.length === 0) {
      loader.destroy();
      return;
    }

    let allFiles = [];
    let filesData = uniqueFiles.map((file) => {
      let rawFile = file.getRawFile();
      allFiles.push(rawFile);
      return {
        name: file.name,
        type: rawFile.type,
        fileId: fileId,
        fieldLabel: selectedFolderName,
      };
    });

    try {
      const response = await postAttachments({ filesData, files: allFiles });
      if (response?.status === "success") {
        setUploadedFiles([]);
        
        // Select the first uploaded file as the active document
        const preferredDocument = response.data?.length > 0 ? response.data[0] : null;
        getDocumentsList(preferredDocument);
        
        alertAdapter.show({ title: "Files Uploaded.", confirmButtonText: "Got it", icon: "success" });
      } else {
        alertAdapter.show({ title: response.message, confirmButtonText: "Got it", icon: "error" });
      }
    } catch (e) {
      alertAdapter.show({ title: "Error: " + (e?.message || e), confirmButtonText: "Got it", icon: "error" });
    } finally {
      loader.destroy();
    }
  };

  return (
    <Card className="border-none prevent-select">
      <CardBody className="card-50 p-0 pr-1">
        {Object.entries(documentsList)?.map(([name, contents]) => {
          return (
            !(entityConfig?.hide_empty_folders && contents.length === 0) && (
              <>
                <ExpansionPanel
                  key={name}
                  className="bg-teal mt-1 p-0 rounded"
                  title={<CustomTitle name={name} />}
                  expanded={selectedFolderName === name}
                  onAction={() => handleExpansion(name)}
                >
                  <Reveal>
                    {selectedFolderName === name && (
                      <ExpansionPanelContent className="bg-white">
                        <div className="content documentslisting">
                          <div className="my-2 doclist-container">
                            {contents?.length > 0 ? (
                              contents.map((attachment) => {
                                if (!attachment?.uuid) return null;
                                return (
                                  <Card
                                    onClick={() => {
                                      onSelectDocument(attachment);
                                    }}
                                    className="border-none"
                                    key={attachment.uuid}
                                  >
                                    <CardBody
                                      className={
                                        attachment?.uuid === selectedDocument?.uuid
                                          ? "pt-1 attachments-card-body cursor-pointer selected"
                                          : "pt-1 attachments-card-body cursor-pointer"
                                      }
                                    >
                                      <i className="fa fa-file fa-lg"></i>
                                      <span title={attachment.originalName} className="content-text ml-2">
                                        {attachment?.originalName?.length < 25 ? attachment.originalName : shortenFilename(attachment.originalName)}
                                      </span>
                                      {attachment?.uuid === selectedDocument?.uuid &&  !entityConfig?.disable_create_upload && (
                                        <i
                                          className="fa fa-xmark float-right"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onDeleteDocument(attachment, name);
                                          }}
                                          title={`Delete ${attachment.originalName}`}
                                        ></i>
                                      )}
                                    </CardBody>
                                  </Card>
                                );
                              })
                            ) : (
                              <Card className="border-none">
                                <CardBody className="attachments-card-body cursor-pointer">
                                  <span className="ml-2">No files found.</span>
                                  {hasStorageType && !entityConfig?.disable_create_upload && (
                                    <span className="ml-2">You can upload files or create a new one using Office to get started.</span>
                                  )}
                                </CardBody>
                              </Card>
                            )}
                          </div>
                        </div>
                        {!entityConfig?.disable_create_upload && (
                          <div className="justify-content-md-center mt-2 row">
                            <div className="col-md-6 text-center">
                              <Button
                                fillMode="solid"
                                themeColor="primary"
                                rounded="full"
                                onClick={() => {
                                  uploadRef?.current?.querySelector(".k-upload-button").click();
                                }}
                                title={`Upload a file`}
                              >
                                <i className="fa fa-plus mr-2"></i>Upload
                              </Button>

                              {/* Visibility set to hidden as Upload happens on button click */}
                              <div ref={uploadRef}>
                                <Upload
                                  batch={true}
                                  multiple={true}
                                  autoUpload={false}
                                  files={uploadedFiles}
                                  onAdd={onFileChange}
                                  onRemove={onFileChange}
                                  restrictions={{
                                    allowedExtensions: extensionTypes,
                                    maxFileSize: 50000000,
                                  }}
                                  style={{ visibility: "hidden" }}
                                  />
                              </div>
                            </div>
                            <div className="col-md-6 text-center">
                              <Button
                                    fillMode={"clear"}
                                    rounded="full"
                                    onClick={toggleDialog}
                                    title={`Create a new file in Office`}
                                    className="clear-bordered"
                                  >
                                    <i className="fa fa-plus mr-2"></i>Create
                                  </Button>
                            </div>
                          </div>
                        )}
                      </ExpansionPanelContent>
                    )}
                  </Reveal>
                </ExpansionPanel>

                {visible && (
                  <Dialog className="custom-dialog" onClose={toggleDialog}>
                    <Card className="">
                      <CardHeader>
                        <div className="row">
                          <div className="col-10">
                            <p className="mb-1">Create a new File in Office</p>
                          </div>
                          <div className="col-2">
                            <span className="card-close" onClick={toggleDialog}>
                              <i className="fa fa-close"></i>
                            </span>
                          </div>
                        </div>
                      </CardHeader>
                      <CardBody>
                        <div style={{ marginBottom: 20 }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <Label editorId="fileName" className="k-form-label" style={{ width: "30%", marginRight: "10px" }}>
                              File name
                            </Label>
                            <div className="k-form-field-wrap" style={{ width: "70%" }}>
                              <Input
                                type="text"
                                id="fileName"
                                value={fileName}
                                onChange={(event) => setFileName(event.target.value)}
                                style={{ width: "100%" }}
                              />
                            </div>
                          </div>
                        </div>

                        <div style={{ marginBottom: 20 }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <Label editorId="fileType" className="k-form-label" style={{ width: "30%", marginRight: "10px" }}>
                              File Type
                            </Label>
                            <div className="k-form-field-wrap" style={{ width: "70%" }}>
                              <DropDownList
                                data={fileTypeOptions}
                                defaultValue={fileTypeOptions[0]}
                                id="fileType"
                                textField="key"
                                dataItemKey="key"
                                value={selectedFiletype}
                                onChange={(event) => setSelectedFiletype(event.value)}
                                style={{ width: "100%" }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="float-right mr-2">
                          <Button
                            fillMode="solid"
                            themeColor="primary"
                            rounded="full"
                            onClick={() => {
                              setSelectedFolderName(name);
                              handleCreate(fileName, selectedFiletype.value);
                            }}
                          >
                            Create file
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  </Dialog>
                )}
              </>
            )
          );
        })}
      </CardBody>
    </Card>
  );
};

// /////////////////////////////////////////////////////////////////////
const DocumentView = ({
  core,
  notif,
  baseUrl,
  fileId,
  appId,
  selectedDocument,
  setSelectedDocument,
  selectedFolderName,
  onDeleteDocument,
  entityConfig,
  handleShareLink,
  handleRename,
  hasStorageType
}) => {
  const [renameErrMsg, setRenameErrMsg] = useState("");
  const viewUrl = core.config("ui.url");
  
  const renderFile = (document) => {
    const type = document?.extension; //document?.type?.split?.("/")?.[1]?.toLowerCase();
    const url = baseUrl + "app/" + appId + "/document/" + document.originalName + "?docPath=" + document.file;
    const buttons = [
      { type: "edit", icon: "fa-edit", text: "Edit Document" },
      { type: "view", icon: "fa-eye", text: "View Document" },
    ];
    switch (type) {
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
        return (
          <div className="img-container">
            <img src={document.url || url} />
          </div>
        );
      case "mp4":
      case "avi":
        return (
          <div className="img-container">
            <video autoplay muted preload controls width="100%">
              <source src={document.url || url} type={"video/" + type} />
              Sorry, your browser doesn't support embedded videos.
            </video>
          </div>
        );
      case "pdf":
        const fileEmpty = !document.file || document?.file?.trim()?.length === 0;
        return (
          <>
            {!fileEmpty && (
              <iframe
                src={(document.url || url) + "?action=preview" + "#toolbar=0"}
                // #toolbar=0 hides the toolbar
                className="iframe"
              />
            )}
          </>
        );
      case "docx":
      case "doc":
      case "xlsx":
      case "xlsm":
      case "xls":
      case "pptx":
      case "ppt":
        if (core.configuration?.storage?.type === "sharepoint") {
          document.officeViewUrl = baseUrl + "attachment/" + fileId + "/" + document.uuid + "/edit";
        } else if (core.configuration?.storage?.type === "onlyoffice") {
          document.officeViewUrl = viewUrl + "?app=EOXOffice" + "&fileId=" + fileId + "&docId=" + document.uuid;
        }
        setSelectedDocument(document);
        return (
          <div className="my-5 py-5 docviewer-wrapper">
            <h4>Sorry, this file cannot be previewed.</h4>
            <br />
            {hasStorageType && !entityConfig?.disable_create_upload && (
              <div className="action-buttons-container">
                <div className="action-buttons-wrapper">
                  {buttons.map((button) => (
                    <Button key={button.type} className="action-button" onClick={() => handleShareLink(button.type)}>
                      <span className="button-content">
                        <i className={`fa ${button.icon}`} />
                        <span className="button-text">{button.text}</span>
                      </span>
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      default:
        return (
          <div className="my-5 py-5">
            <h4>Sorry, this file cannot be previewed.</h4>
            <br />
            <p>Please open the downloaded file on your computer using a supported application.</p>
          </div>
        );
    }
  };

  const onChangeName = (e, document) => {
    const specialCharsRegex = /[.\/\\!@#$%^&*()+=\[\]{};:'"<>?,|]/;
    const newValue = e.target.value;
    try {
      if (specialCharsRegex.test(newValue)) {
        setRenameErrMsg("Special characters are not allowed in the file name");
        return;
      }
      setRenameErrMsg("");
      const edited = { ...document };
      edited["originalName"] = newValue + "." + document?.originalName?.split(".")[1];
      setSelectedDocument({ ...edited });
    } catch (error) {
      setRenameErrMsg(error.message);
    }
  };

  const navigateOrDownload = (e, doc, url) => {
    e.stopPropagation();
    if (doc?.file?.trim()?.length === 0 || !doc.file) {
      window.open(url || doc.url, "_blank").focus();
      return;
    }
    const anchor = document.createElement("a");
    anchor.href = url || doc.url;
    anchor.download = doc.name;
    anchor.target = "_blank";
    anchor.click();
  };

  return (
    <>
      <div className="mb-2">
        <span>
          <input
            type="text"
            id="filename"
            className={`rename-input pl-2 pt-2 ${renameErrMsg ? "k-invalid" : ""}`}
            value={selectedDocument.originalName ? selectedDocument.originalName.split(".")[0] : ""}
            onChange={(e) => {
              onChangeName(e, selectedDocument);
            }}
            onBlur={(e) => {
              setRenameErrMsg("");
              handleRename(e, selectedDocument, selectedFolderName);
            }}
            title={`Rename ${selectedDocument?.originalName}`}
            disabled={(entityConfig?.disable_create_upload)}
          />
          {renameErrMsg && <Error>{renameErrMsg}</Error>}
        </span>
        <div className="float-right">
          {hasStorageType && !entityConfig?.disable_create_upload && (
            <>
              <Button
                className={"ml-1"}
                size={"small"}
                fillMode="clear"
                title="Copy Download URL"
                onClick={() => {
                  notif?.current?.notify("", "Link has been copied to clipboard!", "success", undefined, 3000);
                  core.make("oxzion/link").copyToClipboard(selectedDocument.officeViewUrl ?? selectedDocument.url);
                }}
              >
                <i className="fa fa-copy"></i>
              </Button>
              <Button
                className={"ml-1"}
                size={"small"}
                fillMode="clear"
                title={`Delete ${selectedDocument?.originalName}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteDocument(selectedDocument, selectedFolderName);
                }}
              >
                <i className="fa fa-trash"></i>
              </Button>
            </>
          )}
          <Button
            className={"ml-1"}
            size={"small"}
            fillMode="clear"
            title={`Download ${selectedDocument?.originalName}`}
            onClick={(e) => navigateOrDownload(e, selectedDocument, selectedDocument.url)}
          >
            <i className="fa fa-arrow-down-to-line"></i>
          </Button>
        </div>
      </div>
      <Card>
        <CardBody className="text-center">{renderFile(selectedDocument)}</CardBody>
      </Card>
    </>
  );
};

export default DocumentViewer;
